import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChartBarIcon, 
  GiftIcon, 
  TicketIcon, 
  UserGroupIcon, 
  PlusIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilSquareIcon,
  ArrowPathIcon,
  MapPinIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';
import { adminAPI } from '../services/api';
import { RewardConfig, SupportTicket } from '../types';
import toast from 'react-hot-toast';

type Tab = 'metrics' | 'rewards' | 'tickets' | 'users' | 'early-access' | 'expansion-interests';

interface DisableModalState {
  isOpen: boolean;
  userId: string | null;
  userName: string | null;
  currentDisabled: boolean;
  isTicketResolution?: boolean;
  ticketId?: string | null;
  action?: 'unblock' | 'reject';
  isBulk?: boolean;
  bulkUserIds?: string[];
}

const AdminDashboardPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>('metrics');
  const [metrics, setMetrics] = useState<any>(null);
  const [rewardConfigs, setRewardConfigs] = useState<RewardConfig[]>([]);
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [whitelist, setWhitelist] = useState<any[]>([]);
  const [expansionInterests, setExpansionInterests] = useState<any[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  
  // User Search & Filters
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState<string>('all');
  const [userStatusFilter, setUserStatusFilter] = useState<string>('all');

  // Ticket Filters
  const [ticketFilter, setTicketFilter] = useState<'all' | 'unblock' | 'general'>('all');

  // Reward Modal State
  const [isRewardModalOpen, setIsRewardModalOpen] = useState(false);
  const [editingReward, setEditingReward] = useState<Partial<RewardConfig> | null>(null);

  // Rich Text Reason Modal State
  const [disableModal, setDisableModal] = useState<DisableModalState>({
    isOpen: false,
    userId: null,
    userName: null,
    currentDisabled: false,
  });
  const [richReasonText, setRichReasonText] = useState('');
  const [activeModalTab, setActiveModalTab] = useState<'editor' | 'preview'>('editor');
  const [submittingAction, setSubmittingAction] = useState(false);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'metrics') {
        const res = await adminAPI.getMetrics();
        setMetrics(res.data.data);
      } else if (activeTab === 'rewards') {
        const res = await adminAPI.getRewards();
        setRewardConfigs(res.data.data);
      } else if (activeTab === 'tickets') {
        const res = await adminAPI.getTickets();
        setTickets(res.data.data);
      } else if (activeTab === 'users') {
        const res = await adminAPI.getUsers();
        setUsers(res.data.data);
      } else if (activeTab === 'early-access') {
        const res = await adminAPI.getEarlyAccessWhitelist();
        setWhitelist(res.data.data);
      } else if (activeTab === 'expansion-interests') {
        const res = await adminAPI.getExpansionInterests();
        setExpansionInterests(res.data.data);
      }
    } catch (error) {
      toast.error('Failed to fetch data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSelectUser = (id: string) => {
    setSelectedUserIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleToggleSelectAllUsers = (filteredList: any[]) => {
    if (selectedUserIds.length === filteredList.length && filteredList.length > 0) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(filteredList.map((u) => u.id));
    }
  };

  const handleOpenBulkDisableModal = (isDisabled: boolean) => {
    if (selectedUserIds.length === 0) {
      toast.error('Please select at least one user from the list.');
      return;
    }

    if (!isDisabled) {
      if (window.confirm(`Are you sure you want to re-enable ${selectedUserIds.length} user accounts?`)) {
        setSubmittingAction(true);
        adminAPI.bulkDisableUsers(selectedUserIds, false, '')
          .then(() => {
            toast.success(`Re-enabled ${selectedUserIds.length} user accounts`);
            setSelectedUserIds([]);
            fetchData();
          })
          .catch(() => toast.error('Failed to bulk enable users'))
          .finally(() => setSubmittingAction(false));
      }
      return;
    }

    setDisableModal({
      isOpen: true,
      userId: null,
      userName: `${selectedUserIds.length} Selected User Accounts`,
      currentDisabled: false,
      isBulk: true,
      bulkUserIds: selectedUserIds,
    });
    setRichReasonText(`<p>Your ClickCollab account has been <strong>temporarily disabled</strong> because our platform operations are currently concentrated exclusively within <strong>Bengaluru and a 100km radius</strong>.</p>
<ul>
  <li><strong>Operational Focus:</strong> Bengaluru City & 100km surrounding radius.</li>
  <li><strong>Account Status:</strong> Temporarily paused while operations are localized.</li>
</ul>
<p>We will automatically notify you via email as soon as ClickCollab expands to your location!</p>`);
  };

  const handleUpsertReward = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingReward) return;

    try {
      await adminAPI.upsertReward(editingReward);
      toast.success(editingReward.id ? 'Reward updated' : 'Reward created');
      setIsRewardModalOpen(false);
      setEditingReward(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to save reward');
    }
  };

  const handleDeleteReward = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this reward tier?')) return;
    try {
      await adminAPI.deleteReward(id);
      toast.success('Reward deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete reward');
    }
  };

  const handleUpdateTicketStatus = async (id: string, status: string) => {
    try {
      await adminAPI.updateTicketStatus(id, status);
      toast.success(`Ticket marked as ${status}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update ticket');
    }
  };

  const handleToggleEarlyAccess = async (userId: string, currentStatus: boolean) => {
    try {
      await adminAPI.toggleEarlyAccess(userId, !currentStatus);
      toast.success(`Early access ${!currentStatus ? 'enabled' : 'disabled'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update early access status');
    }
  };

  const handleOpenDisableModal = (user: any) => {
    if (user.is_disabled) {
      if (window.confirm(`Are you sure you want to re-enable ${user.first_name} ${user.last_name}'s account?`)) {
        submitToggleDisable(user.id, false, '');
      }
    } else {
      setDisableModal({
        isOpen: true,
        userId: user.id,
        userName: `${user.first_name} ${user.last_name}`,
        currentDisabled: false,
        isTicketResolution: false,
      });
      setRichReasonText(`<p>Your account has been temporarily disabled due to a violation of platform community policies or security guidelines.</p>\n<ul>\n  <li>Specific Reason: Inappropriate content or policy non-compliance.</li>\n  <li>Action Required: Contact platform administration to appeal.</li>\n</ul>`);
    }
  };

  const submitToggleDisable = async (userId: string, isDisabled: boolean, reasonHtml: string) => {
    setSubmittingAction(true);
    try {
      await adminAPI.toggleDisableUser(userId, isDisabled, reasonHtml);
      toast.success(`User account ${isDisabled ? 'disabled' : 'enabled'} & email dispatched`);
      setDisableModal({ isOpen: false, userId: null, userName: null, currentDisabled: false });
      fetchData();
    } catch (error) {
      toast.error('Failed to update user status');
    } finally {
      setSubmittingAction(false);
    }
  };

  const handleResolveUnblockTicket = async (ticket: any, action: 'unblock' | 'reject') => {
    if (action === 'unblock') {
      if (!window.confirm(`Approve unblock appeal for ${ticket.user?.first_name || 'User'}? Account will be re-enabled.`)) return;
      try {
        await adminAPI.resolveUnblockTicket(ticket.id, 'unblock', '<p>Your account unblock request has been approved by administration.</p>');
        toast.success('Unblock request approved & account restored');
        fetchData();
      } catch (err) {
        toast.error('Failed to approve unblock ticket');
      }
    } else {
      setDisableModal({
        isOpen: true,
        userId: ticket.user_id,
        userName: ticket.user ? `${ticket.user.first_name} ${ticket.user.last_name}` : 'User',
        currentDisabled: true,
        isTicketResolution: true,
        ticketId: ticket.id,
        action: 'reject',
      });
      setRichReasonText(`<p>After reviewing your appeal, platform administration has decided to <strong>keep your account disabled</strong>.</p>\n<p><strong>Reason Details:</strong></p>\n<ul>\n  <li>The appeal did not provide sufficient verification or resolution for the original violation.</li>\n</ul>`);
    }
  };

  const handleModalSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (disableModal.isBulk && disableModal.bulkUserIds) {
      setSubmittingAction(true);
      adminAPI.bulkDisableUsers(disableModal.bulkUserIds, true, richReasonText)
        .then(() => {
          toast.success(`Successfully disabled ${disableModal.bulkUserIds?.length} user accounts & dispatched email notifications`);
          setDisableModal({ isOpen: false, userId: null, userName: null, currentDisabled: false });
          setSelectedUserIds([]);
          fetchData();
        })
        .catch(() => toast.error('Failed to bulk disable user accounts'))
        .finally(() => setSubmittingAction(false));
    } else if (disableModal.isTicketResolution && disableModal.ticketId) {
      setSubmittingAction(true);
      adminAPI.resolveUnblockTicket(disableModal.ticketId, 'reject', richReasonText)
        .then(() => {
          toast.success('Unblock appeal rejected & detailed notification sent');
          setDisableModal({ isOpen: false, userId: null, userName: null, currentDisabled: false });
          fetchData();
        })
        .catch(() => toast.error('Failed to reject appeal'))
        .finally(() => setSubmittingAction(false));
    } else if (disableModal.userId) {
      submitToggleDisable(disableModal.userId, true, richReasonText);
    }
  };

  const insertRichTag = (tagStart: string, tagEnd: string = '') => {
    setRichReasonText((prev) => prev + `${tagStart}${tagEnd}`);
  };

  const handleAddWhitelist = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail) return;
    try {
      await adminAPI.addToEarlyAccessWhitelist(newEmail);
      toast.success('Email added to whitelist');
      setNewEmail('');
      fetchData();
    } catch (error) {
      toast.error('Failed to add email');
    }
  };

  const handleRemoveWhitelist = async (id: string) => {
    if (!window.confirm('Remove this email from whitelist?')) return;
    try {
      await adminAPI.removeFromEarlyAccessWhitelist(id);
      toast.success('Email removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove email');
    }
  };

  const renderMetrics = () => {
    if (!metrics) return null;
    const stats = [
      { name: 'Total Users', value: metrics.total_users, icon: UserGroupIcon, color: 'text-blue-500', bg: 'bg-blue-50' },
      { name: 'Active Projects', value: metrics.active_projects, icon: ChartBarIcon, color: 'text-purple-500', bg: 'bg-purple-50' },
      { name: 'Total Leads', value: metrics.total_leads, icon: UserGroupIcon, color: 'text-green-500', bg: 'bg-green-50' },
      { name: 'Support Tickets', value: metrics.total_support_tickets, icon: TicketIcon, color: 'text-orange-500', bg: 'bg-orange-50' },
      { name: 'New Today', value: metrics.users_today, icon: ClockIcon, color: 'text-pink-500', bg: 'bg-pink-50' },
    ];

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {stats.map((stat, idx) => (
          <motion.div
            key={stat.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
            className="bg-white dark:bg-black/40 p-8 rounded-[2rem] border border-gray-100 dark:border-white/5 flex items-center space-x-6 hover:shadow-2xl hover:shadow-indigo-50/50 dark:hover:shadow-none transition-all group"
          >
            <div className={`p-4 rounded-3xl ${stat.bg} dark:bg-white/5 transition-transform group-hover:scale-110`}>
              <stat.icon className={`h-8 w-8 ${stat.color} dark:text-white`} />
            </div>
            <div>
              <p className="text-sm font-black text-gray-400 uppercase tracking-widest leading-none mb-2">{stat.name}</p>
              <p className="text-4xl font-black text-gray-900 dark:text-white tracking-tighter">{stat.value}</p>
            </div>
          </motion.div>
        ))}
      </div>
    );
  };

  const renderRewards = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-900">Reward Tiers</h2>
        <button
          onClick={() => { setEditingReward({ enabled: true, reward_tier: 'pro', duration_months: 3 }); setIsRewardModalOpen(true); }}
          className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Add Tier</span>
        </button>
      </div>

      <div className="bg-white dark:bg-black/40 rounded-[2.5rem] border border-gray-100 dark:border-white/5 overflow-hidden shadow-xl shadow-gray-50/50 dark:shadow-none">
        <table className="min-w-full divide-y divide-gray-100 dark:divide-white/5">
          <thead className="bg-gray-50 dark:bg-white/5">
            <tr>
              <th className="px-8 py-5 text-left text-xs font-black text-gray-400 uppercase tracking-widest">Target Tier</th>
              <th className="px-8 py-5 text-left text-xs font-black text-gray-400 uppercase tracking-widest">Reward Value</th>
              <th className="px-8 py-5 text-left text-xs font-black text-gray-400 uppercase tracking-widest">Period</th>
              <th className="px-8 py-5 text-left text-xs font-black text-gray-400 uppercase tracking-widest">Availability</th>
              <th className="px-8 py-5 text-right text-xs font-black text-gray-400 uppercase tracking-widest">Operations</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50 dark:divide-white/5 bg-white dark:bg-transparent">
            {rewardConfigs.map((config) => (
              <tr key={config.id} className="group hover:bg-gray-50 dark:hover:bg-white/5 transition-colors">
                <td className="px-8 py-6 whitespace-nowrap">
                  <div className="text-sm font-black text-gray-900 dark:text-white">{config.min_user_count} — {config.max_user_count}</div>
                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">Users Recruited</div>
                </td>
                <td className="px-8 py-6 whitespace-nowrap">
                  <span className={`px-4 py-1.5 rounded-full text-xs font-black tracking-widest uppercase ${
                    config.reward_tier === 'premium' ? 'bg-purple-50 text-purple-600 dark:bg-white/10 dark:text-white' : 'bg-blue-50 text-blue-600 dark:bg-white/10 dark:text-white'
                  }`}>
                    {config.reward_tier}
                  </span>
                </td>
                <td className="px-8 py-6 whitespace-nowrap font-bold text-gray-900 dark:text-white">{config.duration_months} M</td>
                <td className="px-8 py-6 whitespace-nowrap">
                  <div className={`flex items-center space-x-2 text-xs font-black tracking-wide ${config.enabled ? 'text-green-500' : 'text-gray-300'}`}>
                    <div className={`w-1.5 h-1.5 rounded-full ${config.enabled ? 'bg-green-500 animate-pulse' : 'bg-gray-200'}`} />
                    <span>{config.enabled ? 'ACTIVE' : 'INACTIVE'}</span>
                  </div>
                </td>
                <td className="px-8 py-6 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end space-x-3 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button 
                      onClick={() => { setEditingReward(config); setIsRewardModalOpen(true); }} 
                      className="p-2 text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-xl transition-all"
                    >
                      <ExclamationCircleIcon className="h-5 w-5" />
                    </button>
                    <button 
                      onClick={() => handleDeleteReward(config.id)} 
                      className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-all"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const availableRoles = Array.from(new Set(users.map((u) => u.user_type).filter(Boolean)));

  const filteredUsers = users.filter((u) => {
    if (userSearchQuery.trim()) {
      const q = userSearchQuery.toLowerCase().trim();
      const fullName = `${u.first_name || ''} ${u.last_name || ''}`.toLowerCase();
      const email = (u.email || '').toLowerCase();
      const userType = (u.user_type || '').toLowerCase();
      const userId = String(u.id || '').toLowerCase();
      const loc = (u.location || u.address || '').toLowerCase();

      const matchesQuery =
        fullName.includes(q) ||
        email.includes(q) ||
        userType.includes(q) ||
        userId.includes(q) ||
        loc.includes(q);

      if (!matchesQuery) return false;
    }

    if (userRoleFilter !== 'all') {
      if ((u.user_type || '').toLowerCase() !== userRoleFilter.toLowerCase()) {
        return false;
      }
    }

    if (userStatusFilter === 'active') {
      if (u.is_disabled) return false;
    } else if (userStatusFilter === 'disabled') {
      if (!u.is_disabled) return false;
    } else if (userStatusFilter === 'verified') {
      if (!u.is_verified) return false;
    }

    return true;
  });

  const renderUsersList = () => (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-black text-gray-900 dark:text-white uppercase tracking-tighter">System Users</h2>
          <p className="text-xs text-gray-400 font-medium mt-0.5">Filter, search, and bulk manage account statuses across the platform.</p>
        </div>
        <div className="flex items-center space-x-3 self-start sm:self-auto">
          <div className="text-xs font-black text-gray-400 uppercase tracking-widest bg-gray-100 dark:bg-white/5 px-4 py-2 rounded-xl">
            Showing {filteredUsers.length} of {users.length} Accounts
          </div>
        </div>
      </div>

      {/* Bulk Operations Toolbar */}
      {selectedUserIds.length > 0 && (
        <div className="bg-indigo-600 text-white p-4 rounded-3xl flex items-center justify-between shadow-xl animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="flex items-center space-x-3 pl-2">
            <span className="w-8 h-8 rounded-xl bg-white/20 flex items-center justify-center text-xs font-black">
              {selectedUserIds.length}
            </span>
            <span className="text-xs font-black uppercase tracking-wider">
              {selectedUserIds.length === 1 ? '1 Account Selected' : `${selectedUserIds.length} Accounts Selected`}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handleOpenBulkDisableModal(false)}
              className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all"
            >
              Bulk Enable
            </button>
            <button
              onClick={() => handleOpenBulkDisableModal(true)}
              className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all shadow-md"
            >
              Bulk Disable (Regional Focus)
            </button>
            <button
              onClick={() => setSelectedUserIds([])}
              className="px-3 py-2 text-white/70 hover:text-white text-xs font-bold uppercase tracking-wider"
            >
              Deselect All
            </button>
          </div>
        </div>
      )}

      {/* Search & Filter Controls */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 bg-white dark:bg-black/40 p-4 rounded-3xl border border-gray-100 dark:border-white/5 shadow-sm">
        {/* Search Field */}
        <div className="relative">
          <MagnifyingGlassIcon className="h-5 w-5 absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search name, email, role, location..."
            value={userSearchQuery}
            onChange={(e) => setUserSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-2xl text-sm font-medium focus:ring-2 focus:ring-indigo-500 outline-none transition-all dark:text-white placeholder-gray-400"
          />
        </div>

        {/* Role Filter */}
        <div>
          <select
            value={userRoleFilter}
            onChange={(e) => setUserRoleFilter(e.target.value)}
            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-2xl text-sm font-bold text-gray-700 dark:text-gray-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all cursor-pointer"
          >
            <option value="all">All Roles</option>
            {availableRoles.map((role) => (
              <option key={role} value={role}>
                {role.toUpperCase()}
              </option>
            ))}
          </select>
        </div>

        {/* Status Filter */}
        <div>
          <select
            value={userStatusFilter}
            onChange={(e) => setUserStatusFilter(e.target.value)}
            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-2xl text-sm font-bold text-gray-700 dark:text-gray-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all cursor-pointer"
          >
            <option value="all">All Statuses</option>
            <option value="active">Active Accounts</option>
            <option value="disabled">Disabled Accounts</option>
            <option value="verified">Verified Accounts</option>
          </select>
        </div>
      </div>

      <div className="bg-white dark:bg-black/40 rounded-[2.5rem] border border-gray-100 dark:border-white/5 overflow-x-auto shadow-xl shadow-gray-50/50 dark:shadow-none">
        <table className="w-full min-w-[900px] divide-y divide-gray-100 dark:divide-white/5">
          <thead className="bg-gray-50 dark:bg-white/5">
            <tr>
              <th className="px-4 py-5 text-center w-12">
                <input
                  type="checkbox"
                  checked={selectedUserIds.length === filteredUsers.length && filteredUsers.length > 0}
                  onChange={() => handleToggleSelectAllUsers(filteredUsers)}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 h-4 w-4 cursor-pointer"
                />
              </th>
              <th className="px-6 py-5 text-left text-xs font-black text-gray-400 uppercase tracking-widest">Identity</th>
              <th className="px-6 py-5 text-left text-xs font-black text-gray-400 uppercase tracking-widest">Classification</th>
              <th className="px-6 py-5 text-left text-xs font-black text-gray-400 uppercase tracking-widest">Location</th>
              <th className="px-6 py-5 text-left text-xs font-black text-gray-400 uppercase tracking-widest">Verification</th>
              <th className="px-6 py-5 text-right text-xs font-black text-gray-400 uppercase tracking-widest">Access Control</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50 dark:divide-white/5 bg-white dark:bg-transparent">
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-8 py-12 text-center text-gray-400 text-sm font-medium">
                  No users found matching your search or filter criteria.
                </td>
              </tr>
            ) : (
              filteredUsers.map((u) => (
                <tr key={u.id} className={`group hover:bg-gray-50 dark:hover:bg-white/5 transition-colors ${selectedUserIds.includes(u.id) ? 'bg-indigo-50/40 dark:bg-indigo-950/20' : ''}`}>
                  <td className="px-4 py-6 text-center">
                    <input
                      type="checkbox"
                      checked={selectedUserIds.includes(u.id)}
                      onChange={() => handleToggleSelectUser(u.id)}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 h-4 w-4 cursor-pointer"
                    />
                  </td>
                  <td className="px-6 py-6 whitespace-nowrap">
                    <Link to={`/profile/${u.id}`} className="flex items-center group/user hover:opacity-90 transition-opacity">
                      <div className="h-12 w-12 flex-shrink-0 relative group/avatar">
                        {u.profile_pic_signed ? (
                          <img className="h-12 w-12 rounded-2xl object-cover shadow-sm group-hover/user:ring-2 group-hover/user:ring-indigo-500 transition-all" src={u.profile_pic_signed} alt="" />
                        ) : (
                          <div className="h-12 w-12 rounded-2xl bg-gray-100 dark:bg-white/5 flex items-center justify-center group-hover/user:ring-2 group-hover/user:ring-indigo-500 transition-all">
                            <UserGroupIcon className="h-6 w-6 text-gray-300" />
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-black text-gray-900 dark:text-white group-hover/user:text-indigo-600 dark:group-hover/user:text-indigo-400 transition-colors flex items-center gap-1.5">
                          <span>{u.first_name} {u.last_name}</span>
                          <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 opacity-0 group-hover/user:opacity-100 transition-opacity text-indigo-500" />
                        </div>
                        <div className="text-xs font-medium text-gray-400">{u.email}</div>
                      </div>
                    </Link>
                  </td>
                  <td className="px-6 py-6 whitespace-nowrap">
                    <span className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest bg-gray-100 dark:bg-white/5 text-gray-500">
                      {u.user_type}
                    </span>
                  </td>
                  <td className="px-6 py-6 whitespace-nowrap">
                    <div className="flex items-center space-x-1.5 text-gray-600 dark:text-gray-300 text-xs font-semibold">
                      <MapPinIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <span>{u.location || u.address || '—'}</span>
                    </div>
                  </td>
                  <td className="px-6 py-6 whitespace-nowrap">
                    {u.is_verified ? (
                      <div className="flex items-center space-x-2 text-green-500">
                        <CheckCircleIcon className="h-4 w-4" />
                        <span className="text-[10px] font-black uppercase tracking-widest">Verified</span>
                      </div>
                    ) : (
                      <span className="text-xs font-bold text-gray-300 uppercase tracking-widest">Standard</span>
                    )}
                  </td>
                  <td className="px-6 py-6 whitespace-nowrap text-right space-x-2">
                    <button
                      onClick={() => handleToggleEarlyAccess(u.id, u.has_early_access)}
                      className={`px-3 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                        u.has_early_access 
                          ? 'bg-black text-white' 
                          : 'bg-gray-50 dark:bg-white/5 text-gray-400 hover:text-gray-900 dark:hover:text-white'
                      }`}
                    >
                      {u.has_early_access ? 'Override ON' : 'Override Label'}
                    </button>
                    <button
                      onClick={() => handleOpenDisableModal(u)}
                      className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-sm ${
                        u.is_disabled 
                          ? 'bg-emerald-600 text-white hover:bg-emerald-700 font-bold' 
                          : 'bg-rose-600 text-white hover:bg-rose-700 font-bold'
                      }`}
                    >
                      {u.is_disabled ? 'Enable Account' : 'Disable Account'}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderExpansionInterests = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-black text-gray-900 dark:text-white uppercase tracking-tighter">Expansion Interest Requests</h2>
          <p className="text-xs text-gray-400 font-medium mt-0.5">Demands submitted by creators & hirers outside the 100km Bangalore radius.</p>
        </div>
        <div className="text-xs font-black text-gray-400 uppercase tracking-widest bg-gray-100 dark:bg-white/5 px-4 py-2 rounded-xl">
          Total Requests: {expansionInterests.length}
        </div>
      </div>

      <div className="bg-white dark:bg-black/40 rounded-[2.5rem] border border-gray-100 dark:border-white/5 overflow-hidden shadow-xl shadow-gray-50/50 dark:shadow-none">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100 dark:divide-white/5">
            <thead className="bg-gray-50 dark:bg-white/5">
              <tr>
                <th className="px-8 py-5 text-left text-xs font-black text-gray-400 uppercase tracking-widest">Email Identity</th>
                <th className="px-8 py-5 text-left text-xs font-black text-gray-400 uppercase tracking-widest">Requested City / Region</th>
                <th className="px-8 py-5 text-left text-xs font-black text-gray-400 uppercase tracking-widest">GPS Coordinates</th>
                <th className="px-8 py-5 text-right text-xs font-black text-gray-400 uppercase tracking-widest">Submitted Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-white/5 bg-white dark:bg-transparent">
              {expansionInterests.map((item) => (
                <tr key={item.id} className="group hover:bg-gray-50 dark:hover:bg-white/5 transition-colors">
                  <td className="px-8 py-6 whitespace-nowrap">
                    <div className="text-sm font-black text-gray-900 dark:text-white">{item.email}</div>
                  </td>
                  <td className="px-8 py-6 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <MapPinIcon className="h-4 w-4 text-orange-500" />
                      <span className="text-xs font-bold text-gray-800 dark:text-gray-200">
                        {item.city ? `${item.city}${item.state ? `, ${item.state}` : ''}` : 'Location Not Specified'}
                      </span>
                    </div>
                  </td>
                  <td className="px-8 py-6 whitespace-nowrap">
                    {item.latitude && item.longitude ? (
                      <a
                        href={`https://maps.google.com/?q=${item.latitude},${item.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center space-x-1.5 text-indigo-600 dark:text-indigo-400 hover:underline text-xs font-semibold"
                      >
                        <span>{item.latitude.toFixed(4)}, {item.longitude.toFixed(4)}</span>
                        <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                      </a>
                    ) : (
                      <span className="text-xs text-gray-400 font-bold uppercase tracking-wider">No GPS Data</span>
                    )}
                  </td>
                  <td className="px-8 py-6 whitespace-nowrap text-right text-xs font-bold text-gray-400">
                    {new Date(item.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
              {expansionInterests.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-8 py-20 text-center text-gray-400 text-sm font-medium">
                    No regional expansion requests received yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderEarlyAccessWhitelist = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-900">Email Whitelist (Early Access)</h2>
        <div className="text-sm text-gray-500">
          Whitelisted Emails: {whitelist.length}
        </div>
      </div>

      <form onSubmit={handleAddWhitelist} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
        <div className="flex-1">
          <input
            type="email"
            placeholder="Enter email to whitelist"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            required
            className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-black outline-none transition-all"
          />
        </div>
        <button
          type="submit"
          className="px-8 py-2 bg-black text-white font-bold rounded-xl hover:bg-gray-800 transition-all flex items-center justify-center space-x-2 shadow-lg hover:shadow-gray-200"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Whitelist User</span>
        </button>
      </form>

      <div className="bg-white dark:bg-black/40 rounded-[2.5rem] border border-gray-100 dark:border-white/5 overflow-hidden shadow-xl shadow-gray-50/50 dark:shadow-none">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100 dark:divide-white/5">
            <thead className="bg-gray-50 dark:bg-white/5">
              <tr>
                <th className="px-8 py-5 text-left text-xs font-black text-gray-400 uppercase tracking-widest">Email Identity</th>
                <th className="px-8 py-5 text-left text-xs font-black text-gray-400 uppercase tracking-widest">Added Date</th>
                <th className="px-8 py-5 text-right text-xs font-black text-gray-400 uppercase tracking-widest">Privileges</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-white/5 bg-white dark:bg-transparent">
              {whitelist.map((item) => (
                <tr key={item.id} className="group hover:bg-gray-50 dark:hover:bg-white/5 transition-colors">
                  <td className="px-8 py-6 whitespace-nowrap">
                    <div className="text-sm font-black text-gray-900 dark:text-white">{item.email}</div>
                  </td>
                  <td className="px-8 py-6 whitespace-nowrap">
                    <div className="text-sm font-bold text-gray-500">
                      {new Date(item.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                    </div>
                  </td>
                  <td className="px-8 py-6 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleRemoveWhitelist(item.id)}
                      className="p-3 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-2xl transition-all opacity-0 group-hover:opacity-100"
                      title="Revoke access"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </td>
                </tr>
              ))}
              {whitelist.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-8 py-20 text-center">
                    <div className="flex flex-col items-center justify-center text-gray-400">
                      <UserGroupIcon className="h-16 w-16 mb-6 opacity-10" />
                      <p className="text-xl font-black tracking-tight text-gray-900 dark:text-white uppercase">Whitelist is empty</p>
                      <p className="text-sm font-medium mt-1">Authorized emails will appear here.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderTickets = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-gray-900">Support Tickets</h2>
      <div className="grid grid-cols-1 gap-6">
        {tickets.map((ticket, idx) => (
          <motion.div
            key={ticket.id}
            layout
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: idx * 0.05 }}
            className="bg-white dark:bg-black/40 p-8 rounded-[2.5rem] border border-gray-100 dark:border-white/5 flex flex-col md:flex-row md:items-center justify-between group hover:shadow-2xl transition-all"
          >
            <div className="flex-1">
              <div className="flex items-center space-x-4 mb-3">
                <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
                  ticket.status === 'open' ? 'bg-red-50 text-red-600' : 
                  ticket.status === 'in_progress' ? 'bg-yellow-50 text-yellow-600' : 'bg-green-50 text-green-600'
                }`}>
                  {ticket.status.replace('_', ' ')}
                </span>
                <span className="text-xs font-bold text-gray-400">#{ticket.id.slice(0, 8)}</span>
              </div>
              <h3 className="text-xl font-black text-gray-900 dark:text-white tracking-tight mb-2 group-hover:text-indigo-600 transition-colors uppercase">{ticket.subject}</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4 line-clamp-2 text-sm font-medium">{ticket.message}</p>
              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-2">
                  <div className="w-6 h-6 rounded-lg bg-gray-100 dark:bg-white/5 flex items-center justify-center">
                    <UserGroupIcon className="h-3.5 w-3.5 text-gray-500" />
                  </div>
                  <span className="text-xs font-black text-gray-500 uppercase tracking-wide">{ticket.user?.first_name} {ticket.user?.last_name}</span>
                </div>
                <div className="flex items-center space-x-2">
                   <div className="w-6 h-6 rounded-lg bg-gray-100 dark:bg-white/5 flex items-center justify-center">
                    <ClockIcon className="h-3.5 w-3.5 text-gray-500" />
                  </div>
                  <span className="text-xs font-black text-gray-500 uppercase tracking-wide">{new Date(ticket.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3 mt-6 md:mt-0 opacity-0 group-hover:opacity-100 transition-opacity">
              {ticket.status !== 'closed' && (
                <>
                  {ticket.status === 'open' && (
                    <button
                      onClick={() => handleUpdateTicketStatus(ticket.id, 'in_progress')}
                      className="px-6 py-3 bg-gray-900 dark:bg-white text-white dark:text-black rounded-2xl text-xs font-black uppercase tracking-widest hover:scale-105 transition-transform"
                    >
                      Process
                    </button>
                  )}
                  <button
                    onClick={() => handleUpdateTicketStatus(ticket.id, 'closed')}
                    className="px-6 py-3 bg-indigo-600 text-white rounded-2xl text-xs font-black uppercase tracking-widest hover:scale-105 transition-transform shadow-lg shadow-indigo-100"
                  >
                    Resolve
                  </button>
                </>
              )}
            </div>
          </motion.div>
        ))}
        {tickets.length === 0 && (
          <div className="text-center py-20 bg-gray-50 dark:bg-white/5 rounded-[3rem] border-4 border-dashed border-gray-100 dark:border-white/5">
            <TicketIcon className="h-16 w-16 text-gray-200 dark:text-white/10 mx-auto mb-6" />
            <p className="text-xl font-black text-gray-900 dark:text-white uppercase tracking-tighter">Clear Queue</p>
            <p className="text-sm text-gray-400 font-medium mt-1">No pending support requests.</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex dark:bg-gray-900 transition-colors duration-300">
      {/* Premium Sidebar */}
      <aside className="w-72 bg-white dark:bg-black border-r border-gray-100 dark:border-white/5 flex flex-col fixed h-full z-40 overflow-y-auto">
        <div className="p-8">
          <div className="flex items-center space-x-3 mb-8">
            <div className="w-10 h-10 bg-indigo-600 rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-200">
              <ExclamationCircleIcon className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-xl font-black text-gray-900 dark:text-white tracking-tight">Admin<span className="text-indigo-600">Portal</span></h1>
          </div>

          <nav className="space-y-2">
            {[
              { id: 'metrics', name: 'Metrics', icon: ChartBarIcon, color: 'indigo' },
              { id: 'rewards', name: 'Rewards', icon: GiftIcon, color: 'purple' },
              { id: 'tickets', name: 'Tickets', icon: TicketIcon, color: 'orange' },
              { id: 'users', name: 'Users', icon: UserGroupIcon, color: 'blue' },
              { id: 'expansion-interests', name: 'Expansion Req.', icon: MapPinIcon, color: 'purple' },
              { id: 'early-access', name: 'Whitelist', icon: CheckCircleIcon, color: 'emerald' },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id as Tab)}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-2xl text-sm font-bold transition-all duration-300 group ${
                  activeTab === item.id 
                    ? `bg-${item.id === 'early-access' ? 'black' : 'indigo-600'} text-white shadow-xl shadow-gray-200 dark:shadow-none translate-x-1` 
                    : 'text-gray-500 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-white/5'
                }`}
              >
                <item.icon className={`h-5 w-5 ${activeTab === item.id ? 'text-white' : 'text-gray-400 group-hover:text-gray-600'}`} />
                <span>{item.name}</span>
                {activeTab === item.id && (
                  <motion.div layoutId="activePill" className="ml-auto w-1.5 h-1.5 bg-white rounded-full" />
                )}
              </button>
            ))}
          </nav>
        </div>

        <div className="mt-auto p-8 border-t border-gray-50 dark:border-white/5">
          <div className="bg-gray-50 dark:bg-white/5 rounded-2xl p-4">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Status</p>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-sm font-bold text-gray-900 dark:text-white">System Active</span>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 ml-72 min-h-screen">
        {/* Top bar for dashboard */}
        <header className="h-24 bg-white/80 dark:bg-black/80 backdrop-blur-xl border-b border-gray-100 dark:border-white/5 flex items-center justify-between px-10 sticky top-0 z-30">
          <div>
            <h2 className="text-sm font-bold text-gray-400 uppercase tracking-widest leading-none">Management</h2>
            <h3 className="text-2xl font-black text-gray-900 dark:text-white capitalize tracking-tight mt-1">
              {activeTab === 'early-access' ? 'Whitelist Access' : activeTab === 'expansion-interests' ? 'Regional Expansion Requests' : activeTab}
            </h3>
          </div>
          
          <div className="flex items-center space-x-4">
             {loading && <div className="animate-spin rounded-full h-5 w-5 border-2 border-indigo-600 border-t-transparent" />}
             <div className="w-10 h-10 rounded-2xl bg-gray-100 dark:bg-white/5 flex items-center justify-center text-gray-500">
                <UserGroupIcon className="h-5 w-5" />
             </div>
          </div>
        </header>

        <div className="p-10">
          {loading && !metrics && !rewardConfigs.length && !tickets.length && !users.length && !whitelist.length && !expansionInterests.length ? (
            <div className="flex flex-col items-center justify-center h-[60vh]">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
              <p className="text-gray-400 font-medium">Synchronizing system data...</p>
            </div>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                transition={{ duration: 0.3, ease: "circOut" }}
                className="max-w-6xl"
              >
                {activeTab === 'metrics' && renderMetrics()}
                {activeTab === 'rewards' && renderRewards()}
                {activeTab === 'tickets' && renderTickets()}
                {activeTab === 'users' && renderUsersList()}
                {activeTab === 'expansion-interests' && renderExpansionInterests()}
                {activeTab === 'early-access' && renderEarlyAccessWhitelist()}
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </main>

      {/* Reward Modal */}
      {isRewardModalOpen && editingReward && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-xl">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="bg-white dark:bg-gray-900 rounded-[2.5rem] p-10 max-w-lg w-full shadow-2xl border border-white/10"
          >
            <div className="flex justify-between items-center mb-8">
              <h3 className="text-3xl font-black text-gray-900 dark:text-white tracking-tight">{editingReward.id ? 'Modify Tier' : 'New Reward'}</h3>
              <button 
                onClick={() => setIsRewardModalOpen(false)} 
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/5 text-gray-400 transition-colors"
              >
                <XCircleIcon className="h-8 w-8" />
              </button>
            </div>

            <form onSubmit={handleUpsertReward} className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2">Target Range</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="number"
                      required
                      placeholder="Min"
                      value={editingReward.min_user_count}
                      onChange={(e) => setEditingReward({...editingReward, min_user_count: parseInt(e.target.value)})}
                      className="w-full px-5 py-3 bg-gray-50 dark:bg-white/5 border-none rounded-2xl focus:ring-2 focus:ring-indigo-600 outline-none text-gray-900 dark:text-white font-bold"
                    />
                    <span className="text-gray-300">/</span>
                    <input
                      type="number"
                      required
                      placeholder="Max"
                      value={editingReward.max_user_count}
                      onChange={(e) => setEditingReward({...editingReward, max_user_count: parseInt(e.target.value)})}
                      className="w-full px-5 py-3 bg-gray-50 dark:bg-white/5 border-none rounded-2xl focus:ring-2 focus:ring-indigo-600 outline-none text-gray-900 dark:text-white font-bold"
                    />
                  </div>
                </div>
                <div>
                   <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2">Subscription</label>
                   <select
                    value={editingReward.reward_tier}
                    onChange={(e) => setEditingReward({...editingReward, reward_tier: e.target.value})}
                    className="w-full px-5 py-3 bg-gray-50 dark:bg-white/5 border-none rounded-2xl focus:ring-2 focus:ring-indigo-600 outline-none text-gray-900 dark:text-white font-bold appearance-none"
                  >
                    <option value="pro">Pro Membership</option>
                    <option value="premium">Premium Access</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2">Validity Duration</label>
                <div className="relative">
                  <input
                    type="number"
                    required
                    value={editingReward.duration_months}
                    onChange={(e) => setEditingReward({...editingReward, duration_months: parseInt(e.target.value)})}
                    className="w-full px-5 py-3 bg-gray-50 dark:bg-white/5 border-none rounded-2xl focus:ring-2 focus:ring-indigo-600 outline-none text-gray-900 dark:text-white font-bold"
                  />
                  <span className="absolute right-5 top-1/2 -translate-y-1/2 text-sm font-bold text-gray-400">Months</span>
                </div>
              </div>

              <div>
                <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2">Description / Benefit</label>
                <textarea
                  value={editingReward.description}
                  placeholder="Explain what this tier offers..."
                  onChange={(e) => setEditingReward({...editingReward, description: e.target.value})}
                  className="w-full px-5 py-3 bg-gray-50 dark:bg-white/5 border-none rounded-2xl focus:ring-2 focus:ring-indigo-600 outline-none h-28 resize-none text-gray-900 dark:text-white font-medium"
                />
              </div>

              <label className="flex items-center space-x-3 cursor-pointer group">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={editingReward.enabled}
                    onChange={(e) => setEditingReward({...editingReward, enabled: e.target.checked})}
                    className="sr-only"
                  />
                  <div className={`w-12 h-6 rounded-full transition-colors ${editingReward.enabled ? 'bg-green-500' : 'bg-gray-200 dark:bg-white/10'}`} />
                  <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${editingReward.enabled ? 'translate-x-6' : ''}`} />
                </div>
                <span className="text-sm font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Activate Reward Tier</span>
              </label>

              <button
                type="submit"
                className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-black hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-100 dark:shadow-none hover:scale-[1.02] transform active:scale-95"
              >
                {editingReward.id ? 'Commit Changes' : 'Initialize Reward Tier'}
              </button>
            </form>
          </motion.div>
        </div>
      )}

      {/* Rich Text Reason Modal for Account Disabling & Appeal Rejection */}
      {disableModal.isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-xl">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="bg-white dark:bg-gray-900 rounded-[2.5rem] p-8 max-w-2xl w-full shadow-2xl border border-gray-100 dark:border-white/10 overflow-hidden"
          >
            <div className="flex justify-between items-center mb-6">
              <div>
                <span className="px-3 py-1 bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300 font-black text-[10px] uppercase tracking-widest rounded-full">
                  {disableModal.isTicketResolution ? 'Appeal Resolution Details' : 'Account Disabling Reason'}
                </span>
                <h3 className="text-2xl font-black text-gray-900 dark:text-white tracking-tight mt-1">
                  {disableModal.isTicketResolution ? `Reject Appeal for ${disableModal.userName}` : `Disable Account: ${disableModal.userName}`}
                </h3>
              </div>
              <button
                onClick={() => setDisableModal({ isOpen: false, userId: null, userName: null, currentDisabled: false })}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/5 text-gray-400 transition-colors"
              >
                <XCircleIcon className="h-7 w-7" />
              </button>
            </div>

            {/* Modal Tabs: Editor vs Live Email Preview */}
            <div className="flex items-center space-x-3 mb-4 border-b border-gray-100 dark:border-white/10 pb-3">
              <button
                onClick={() => setActiveModalTab('editor')}
                className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center space-x-2 ${
                  activeModalTab === 'editor' ? 'bg-indigo-600 text-white' : 'text-gray-500 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <PencilSquareIcon className="h-4 w-4" />
                <span>Rich Text Editor</span>
              </button>
              <button
                onClick={() => setActiveModalTab('preview')}
                className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center space-x-2 ${
                  activeModalTab === 'preview' ? 'bg-indigo-600 text-white' : 'text-gray-500 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <EyeIcon className="h-4 w-4" />
                <span>Email Live Preview</span>
              </button>
            </div>

            <form onSubmit={handleModalSubmit} className="space-y-4">
              {activeModalTab === 'editor' ? (
                <div>
                  {/* Rich Text Formatting Toolbar */}
                  <div className="flex flex-wrap items-center gap-1.5 p-2 bg-gray-100 dark:bg-white/5 rounded-t-2xl border border-gray-200 dark:border-white/10 border-b-0">
                    <button
                      type="button"
                      onClick={() => insertRichTag('<b>', '</b>')}
                      className="px-2.5 py-1 bg-white dark:bg-black text-gray-800 dark:text-white font-extrabold text-xs rounded-lg hover:bg-gray-200 transition-colors shadow-sm"
                      title="Bold"
                    >
                      B
                    </button>
                    <button
                      type="button"
                      onClick={() => insertRichTag('<i>', '</i>')}
                      className="px-2.5 py-1 bg-white dark:bg-black text-gray-800 dark:text-white italic font-serif text-xs rounded-lg hover:bg-gray-200 transition-colors shadow-sm"
                      title="Italic"
                    >
                      I
                    </button>
                    <button
                      type="button"
                      onClick={() => insertRichTag('<u>', '</u>')}
                      className="px-2.5 py-1 bg-white dark:bg-black text-gray-800 dark:text-white underline text-xs rounded-lg hover:bg-gray-200 transition-colors shadow-sm"
                      title="Underline"
                    >
                      U
                    </button>
                    <div className="h-4 w-px bg-gray-300 dark:bg-white/20 mx-1" />
                    <button
                      type="button"
                      onClick={() => insertRichTag('<h3>', '</h3>')}
                      className="px-2.5 py-1 bg-white dark:bg-black text-gray-800 dark:text-white font-bold text-xs rounded-lg hover:bg-gray-200 transition-colors shadow-sm"
                      title="Heading"
                    >
                      H3
                    </button>
                    <button
                      type="button"
                      onClick={() => insertRichTag('<ul>\n  <li>', '</li>\n</ul>')}
                      className="px-2.5 py-1 bg-white dark:bg-black text-gray-800 dark:text-white font-bold text-xs rounded-lg hover:bg-gray-200 transition-colors shadow-sm"
                      title="Bullet List"
                    >
                      • Bullet List
                    </button>
                    <button
                      type="button"
                      onClick={() => insertRichTag('<ol>\n  <li>', '</li>\n</ol>')}
                      className="px-2.5 py-1 bg-white dark:bg-black text-gray-800 dark:text-white font-bold text-xs rounded-lg hover:bg-gray-200 transition-colors shadow-sm"
                      title="Numbered List"
                    >
                      1. List
                    </button>
                    <button
                      type="button"
                      onClick={() => insertRichTag('<mark style="background-color: #fef08a; padding: 2px 4px;">', '</mark>')}
                      className="px-2.5 py-1 bg-yellow-200 text-yellow-900 font-bold text-xs rounded-lg hover:bg-yellow-300 transition-colors shadow-sm"
                      title="Highlight text"
                    >
                      Highlight
                    </button>
                  </div>

                  <textarea
                    rows={8}
                    value={richReasonText}
                    onChange={(e) => setRichReasonText(e.target.value)}
                    placeholder="Enter detailed formatted HTML reason explaining the decision..."
                    className="w-full p-4 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-b-2xl focus:ring-2 focus:ring-indigo-600 outline-none font-mono text-xs text-gray-900 dark:text-white resize-none"
                    required
                  />
                </div>
              ) : (
                <div className="p-6 bg-orange-50/50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-900/40 rounded-2xl max-h-80 overflow-y-auto">
                  <span className="text-[10px] font-black uppercase text-orange-600 tracking-widest block mb-2">Live Email Template Render:</span>
                  <div 
                    className="prose dark:prose-invert max-w-none text-sm text-gray-800 dark:text-gray-200"
                    dangerouslySetInnerHTML={{ __html: richReasonText || '<em>No reason text specified yet.</em>' }}
                  />
                </div>
              )}

              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-100 dark:border-white/10">
                <button
                  type="button"
                  onClick={() => setDisableModal({ isOpen: false, userId: null, userName: null, currentDisabled: false })}
                  className="px-6 py-3 rounded-2xl text-xs font-bold text-gray-500 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors uppercase tracking-wider"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingAction}
                  className="px-8 py-3 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-2xl text-xs font-black uppercase tracking-wider hover:opacity-90 transition-all shadow-xl shadow-orange-500/20 disabled:opacity-50"
                >
                  {submittingAction ? 'Processing & Emailing...' : disableModal.isTicketResolution ? 'Confirm & Send Rejection Email' : 'Disable Account & Send Email'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboardPage;
